import classNames from 'classnames';
import { AnimatePresence, motion } from 'framer-motion';
import type { ReactNode, SetStateAction } from 'react';
import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import * as styles from './RightPanel.module.scss';
import Button from '../../components/Button';
import IconButton from '../../components/IconButton';
import StickyHeader from '../../components/StickyHeader';
import SVGIcon from '../../components/SVGIcon';
import closeIcon from '../../icons/non-prefilled/16x16/clear.svg';
import useIsLayoutSize from '../../post/utils/useIsLayoutSize';
import { LayoutSize } from '../../post/utils/useLayoutSize';
import addTestId from '../../utils/addTestId';

const ANIMATION_DURATION_MS = parseInt(styles.layoutAnimationDurationMs, 10);
const RIGHT_PANEL_WIDTH = parseInt(styles.rightPanelWidth, 10);
const RIGHT_PANEL_MOBILE_WIDTH = parseInt(styles.rightPanelMobileWidth, 10);

type RightPanelState = {
  node: JSX.Element;
  width: number | null;
  config: { isResizable?: boolean; initialWidth?: number };
};

const RightPanelContext = createContext<{
  rightPanelWidth: number;
  rightPanelState: RightPanelState | null;
  setRightPanelState: React.Dispatch<
    React.SetStateAction<RightPanelState | null>
  >;
}>({
  rightPanelWidth: 0,
  rightPanelState: null,
  setRightPanelState: () => {},
});

interface RightPanelProviderProps {
  children: React.ReactNode;

  /**
   * Used for Storybook only
   */
  initialRightPanel?: JSX.Element;
}

const RightPanelProvider = ({
  children,
  initialRightPanel,
}: RightPanelProviderProps) => {
  const parent = useContext(RightPanelContext);
  const isMobile = useIsLayoutSize(LayoutSize.mobile);

  const [rightPanelState, setRightPanelState] =
    useState<RightPanelState | null>(
      initialRightPanel
        ? {
            node: initialRightPanel,
            width: null,
            config: {},
          }
        : null,
    );

  if (rightPanelState != null && parent.rightPanelState != null) {
    setRightPanelState(null);
  }

  const parentAwareSetRightPanelState = useCallback(
    (newRightPanelState: SetStateAction<RightPanelState | null>) => {
      if (typeof newRightPanelState === 'function') {
        setRightPanelState((current) => {
          const newState = newRightPanelState(current);
          if (newState) {
            parent.setRightPanelState(null);
          }
          return newState;
        });
      }

      if (newRightPanelState) {
        parent.setRightPanelState(null);
      }
      setRightPanelState(newRightPanelState);
    },
    [parent, setRightPanelState],
  );

  const value = useMemo(() => {
    return {
      rightPanelState,
      setRightPanelState: parentAwareSetRightPanelState,
      rightPanelWidth: (() => {
        if (rightPanelState) {
          return (
            rightPanelState.width ??
            (isMobile ? RIGHT_PANEL_MOBILE_WIDTH : RIGHT_PANEL_WIDTH)
          );
        }

        if (parent.rightPanelState) {
          return (
            parent.rightPanelState.width ??
            (isMobile ? RIGHT_PANEL_MOBILE_WIDTH : RIGHT_PANEL_WIDTH)
          );
        }

        return 0;
      })(),
    };
  }, [rightPanelState, parentAwareSetRightPanelState, parent, isMobile]);

  return (
    <RightPanelContext.Provider value={value}>
      {children}
    </RightPanelContext.Provider>
  );
};

export const useRightPanel = () => {
  const { rightPanelState, setRightPanelState } = useContext(RightPanelContext);

  const isRightPanelOpen = !!rightPanelState;

  const openRightPanel = useCallback(
    (node: JSX.Element, config?: RightPanelState['config']) => {
      setRightPanelState({
        node,
        width: config?.initialWidth ?? null,
        config: config ?? {},
      });
    },
    [setRightPanelState],
  );

  const closeRightPanel = useCallback(() => {
    setRightPanelState(null);
  }, [setRightPanelState]);

  return { isRightPanelOpen, openRightPanel, closeRightPanel };
};

export const useRightPanelWidth = () => {
  const { rightPanelWidth } = useContext(RightPanelContext);
  return rightPanelWidth;
};

const RightPanelRoot = () => {
  const { rightPanelState, setRightPanelState } = useContext(RightPanelContext);

  const width = rightPanelState?.width ?? undefined;
  const isOpen = !!rightPanelState;
  const isResizable = rightPanelState?.config.isResizable ?? false;
  const rootRef = useRef<HTMLDivElement>(null);
  const [isResizing, setIsResizing] = useState(false);
  const startWidthRef = useRef<number | null>(rightPanelState?.width ?? null);
  const deltaRef = useRef(0);

  useEffect(() => {
    if (!isResizing || !isResizable) return undefined;

    const handleResizeEnd = () => setIsResizing(false);
    const handleResizeMove = (event: MouseEvent) => {
      const startWidth = startWidthRef.current;
      if (!startWidth) return;

      deltaRef.current += event.movementX;
      setRightPanelState((current) => {
        if (!current) return current;
        return {
          ...current,
          width: Math.max(startWidth - deltaRef.current, RIGHT_PANEL_WIDTH),
        };
      });
    };

    document.addEventListener('mouseup', handleResizeEnd);
    document.addEventListener('mousemove', handleResizeMove);

    return () => {
      document.removeEventListener('mouseup', handleResizeEnd);
      document.removeEventListener('mousemove', handleResizeMove);
    };
  }, [isResizable, isResizing, setRightPanelState]);

  const handleResizeStart = useCallback(() => {
    const element = rootRef.current;
    if (!element) return;

    deltaRef.current = 0;
    startWidthRef.current = element.clientWidth;
    setIsResizing(true);
  }, []);

  return (
    <div className={styles.rightPanelRootContainer}>
      <div
        data-no-print
        ref={rootRef}
        className={classNames(styles.rightPanelRoot, {
          [styles.isOpen]: isOpen,
          [styles.isResizing]: isResizing,
        })}
        style={{ width }}
      >
        {isResizable && isOpen && (
          <Button
            className={styles.resizeHandle}
            onMouseDown={handleResizeStart}
          />
        )}

        {/* Keep the right panel content visible during close animation */}
        <AnimatePresence>
          {!!rightPanelState && (
            <motion.div
              key="view-container"
              exit={{ display: 'none' }}
              transition={{ duration: ANIMATION_DURATION_MS / 1000 }}
              className={styles.rightPanelRootContent}
              style={{ width }}
            >
              {rightPanelState.node}
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </div>
  );
};

interface RightPanelInnerProps {
  children?: ReactNode;
  footer?: ReactNode;
  header: ReactNode;
}

const RightPanelInner = ({
  children,
  footer,
  header,
}: RightPanelInnerProps) => {
  return (
    <div
      className={styles.rightPanelInnerContainer}
      {...addTestId('rightPanel')}
    >
      <div className={styles.rightPanelInner}>
        {header}
        <div className={styles.rightPanelInnerContent}>{children}</div>
      </div>
      {!!footer && <div>{footer}</div>}
    </div>
  );
};

interface RightPanelHeaderProps {
  title: string;
  controls?: ReactNode;
}

const RightPanelHeader = ({ title, controls }: RightPanelHeaderProps) => {
  const { closeRightPanel } = useRightPanel();

  return (
    <StickyHeader>
      <div className={styles.rightPanelHeader}>
        <h4>{title}</h4>
        {!!controls && <div className={styles.headerControls}>{controls}</div>}
        <IconButton
          className={styles.closeButton}
          onClick={closeRightPanel}
          aria-label="Close right panel"
        >
          {closeIcon}
        </IconButton>
      </div>
    </StickyHeader>
  );
};

type RightPanelIconButtonProps = Omit<
  React.ComponentProps<typeof Button>,
  'children' | 'className'
> & {
  icon: string;
};

const RightPanelIconButton = React.forwardRef<
  HTMLButtonElement,
  RightPanelIconButtonProps
>(({ icon, ...rest }, ref) => {
  return (
    <Button {...rest} className={styles.iconButton} ref={ref}>
      <SVGIcon size={12}>{icon}</SVGIcon>
    </Button>
  );
});

interface RightPanelNewHeaderProps {
  title: React.ReactNode;
  iconButtonsExtra?: React.ReactNode;
}

const RightPanelNewHeader = ({
  title,
  iconButtonsExtra,
}: RightPanelNewHeaderProps) => {
  const { closeRightPanel } = useRightPanel();

  return (
    <StickyHeader>
      <div className={styles.rightPanelNewHeader}>
        <h4>{title}</h4>
        <div className={styles.iconButtons}>
          {iconButtonsExtra}
          <Button
            className={styles.iconButton}
            onClick={closeRightPanel}
            aria-label="Close right panel"
          >
            <SVGIcon size={12}>{closeIcon}</SVGIcon>
          </Button>
        </div>
      </div>
    </StickyHeader>
  );
};

export default {
  Provider: RightPanelProvider,
  Root: RightPanelRoot,
  Header: RightPanelHeader,
  NewHeader: RightPanelNewHeader,
  Inner: RightPanelInner,
  IconButton: RightPanelIconButton,
};
